import { API_ENDPOINTS, DEFAULT_PAGE_SIZE, ENTITY_STATUS } from '@/constants';
import { apiRequest } from '@/services/client';
import {
  validateRoleSegmentsResponse,
  validateSegmentResponse,
} from '@/services/shared/contractValidation';
import { mapApiListResponse } from '@/services/shared/listResponse';
import type { ApiListResponse } from '@/types/api';
import type {
  RoleSegment,
  RoleSegmentPayload,
  Segment,
  SegmentListParams,
  SegmentListResult,
  SegmentPayload,
} from '@/types/segment';
import { normalizeUiPageFromParams } from '@/utils/pagination';

type SegmentListApiResponse =
  | SegmentApiItem[]
  | ApiListResponse<SegmentApiItem>;

type SegmentApiItem = Partial<Segment> & {
  segment_id?: number | string;
  segmentId?: number | string;
};

const normalizeItem = (item: SegmentApiItem, index: number): Segment => {
  const apiId = item.id ?? item.segment_id ?? item.segmentId;
  const parsedId =
    typeof apiId === 'string' ? Number.parseInt(apiId, 10) : apiId;
  const discountPercentageCap = item.discount_percentage_cap ?? 0;

  return {
    name: (item.name as string) ?? '',
    label: (item.label as string) ?? '',
    status: (item.status as Segment['status']) ?? '',
    monthly_recharge_enabled:
      item.monthly_recharge_enabled as Segment['monthly_recharge_enabled'],
    allocated_balance: item.allocated_balance,
    created_at: item.created_at,
    updated_at: item.updated_at,
    // Generate id if missing (use index+1 as fallback for display purposes)
    id: Number.isFinite(parsedId) ? (parsedId as number) : index + 1,
    discount_percentage_cap: Number(discountPercentageCap),
  };
};

const mapListResponse = (
  response: SegmentListApiResponse,
  params: SegmentListParams,
): SegmentListResult => {
  return mapApiListResponse<SegmentApiItem, Segment>(response, params, {
    defaultPageSize: DEFAULT_PAGE_SIZE,
    mapItem: (item, index) => {
      const normalized = normalizeItem(item, index);
      return validateSegmentResponse(normalized, {
        operation: 'fetch_segments_list_item',
        endpoint: API_ENDPOINTS.SEGMENTS.LIST,
        itemIndex: index,
      });
    },
  });
};

const normalizeStatusParam = (status?: ENTITY_STATUS | ENTITY_STATUS[]) => {
  if (!status) return undefined;
  return Array.isArray(status) ? status.join(',') : status;
};

export const fetchSegment = async (
  params: SegmentListParams = {},
): Promise<SegmentListResult> => {
  const normalizedPage = normalizeUiPageFromParams(params);

  const nameParam = params.name ?? params.search;

  const requestParams: Record<string, unknown> = {
    page: normalizedPage,
    size: params.size,
    status: normalizeStatusParam(params.status),
  };
  if (nameParam && nameParam.trim() !== '') {
    requestParams.name = nameParam;
  }

  const response = await apiRequest<SegmentListApiResponse>(
    API_ENDPOINTS.SEGMENTS.LIST,
    { params: requestParams, retry: { retries: 1 } },
  );

  return mapListResponse(response, params);
};

export const createSegment = async (
  payload: SegmentPayload,
): Promise<Segment> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.SEGMENTS.CREATE, {
    method: 'POST',
    data: payload,
    retry: { retries: 0 },
  });
  return validateSegmentResponse(response, {
    operation: 'create_segment',
    endpoint: API_ENDPOINTS.SEGMENTS.CREATE,
  });
};

export const updateSegment = async (
  id: number,
  payload: SegmentPayload,
): Promise<Segment> => {
  const discountPercentageCap = payload.discount_percentage_cap ?? 0;

  const response = await apiRequest<unknown>(API_ENDPOINTS.SEGMENTS.UPDATE, {
    method: 'PUT',
    data: {
      id,
      name: payload.name,
      label: payload.label,
      discount_percentage_cap: discountPercentageCap,
      allocated_balance: payload.allocated_balance ?? null,
      monthly_recharge_enabled: payload.monthly_recharge_enabled,
      status: payload.status,
    },
    retry: { retries: 0 },
  });
  return validateSegmentResponse(response, {
    operation: 'update_segment',
    endpoint: API_ENDPOINTS.SEGMENTS.UPDATE,
  });
};

export const deleteSegment = async (id: number): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.SEGMENTS.DELETE(id), {
    method: 'DELETE',
    retry: { retries: 0 },
  });
};

export const reactivateSegment = async (id: number): Promise<Segment> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.SEGMENTS.REACTIVATE(id),
    {
      method: 'PATCH',
      retry: { retries: 0 },
    },
  );
  return validateSegmentResponse(response, {
    operation: 'reactivate_segment',
    endpoint: API_ENDPOINTS.SEGMENTS.REACTIVATE(id),
  });
};

export const fetchRoleSegments = async (options?: {
  useGlobalErrorHandler?: boolean;
}): Promise<RoleSegment[]> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.ROLES.SEGMENTS, {
    method: 'GET',
    retry: { retries: 1 },
    useGlobalErrorHandler: options?.useGlobalErrorHandler,
  });
  return validateRoleSegmentsResponse(response, {
    operation: 'fetch_role_segments',
    endpoint: API_ENDPOINTS.ROLES.SEGMENTS,
  });
};

export const updateRoleSegments = async (
  payload: RoleSegmentPayload,
): Promise<RoleSegment[]> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.ROLES.SEGMENTS, {
    method: 'PUT',
    data: payload,
    retry: { retries: 0 },
  });
  return validateRoleSegmentsResponse(response, {
    operation: 'update_role_segments',
    endpoint: API_ENDPOINTS.ROLES.SEGMENTS,
  });
};
